<script setup lang="ts">
import type { Components } from '@/api/generated/api';
import { ProductFeature } from '@/api/generated/api.enum';
import AnyIcon from '@/core/elements/AnyIcon.vue';
import CustomButton from '@/core/elements/CustomButton.vue';
import { EIcon } from 'common/enum/misc.enum';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

type OverviewFeatureKey =
  | 'customization'
  | 'development'
  | 'gradeWeighting'
  | 'reports'
  | 'reportsAndSupportMeasures'
  | 'roles'
  | 'supportMeasures';

type OverviewFeatureConfig = {
  titleKey: string;
  subtitleKey?: string;
  bulletKeys: OverviewFeatureKey[];
};

const featureTranslationKeys: Record<OverviewFeatureKey, string> = {
  customization: 'billing.checkoutProductDialog.overview.features.customization',
  development: 'billing.checkoutProductDialog.overview.features.development',
  gradeWeighting: 'billing.checkoutProductDialog.overview.features.gradeWeighting',
  reports: 'billing.checkoutProductDialog.overview.features.reports',
  reportsAndSupportMeasures: 'billing.checkoutProductDialog.overview.features.reportsAndSupportMeasures',
  roles: 'billing.checkoutProductDialog.overview.features.roles',
  supportMeasures: 'billing.checkoutProductDialog.overview.features.supportMeasures',
};

const fallbackOverviewConfig: OverviewFeatureConfig = {
  titleKey: 'billing.checkoutProductDialog.overview.fallback.title',
  bulletKeys: ['reportsAndSupportMeasures', 'gradeWeighting', 'development', 'roles', 'customization'],
};

const overviewConfigByFeature: Partial<Record<Components.Schemas.ProductFeature, OverviewFeatureConfig>> = {
  [ProductFeature.attachments]: {
    titleKey: 'billing.checkoutProductDialog.overview.attachments.title',
    subtitleKey: 'billing.checkoutProductDialog.overview.additionalDescription',
    bulletKeys: ['reportsAndSupportMeasures', 'gradeWeighting', 'development', 'roles', 'customization'],
  },
  [ProductFeature.gradeCustomization]: {
    titleKey: 'billing.checkoutProductDialog.overview.gradeWeighting.title',
    subtitleKey: 'billing.checkoutProductDialog.overview.additionalDescription',
    bulletKeys: ['reportsAndSupportMeasures', 'development', 'roles', 'customization'],
  },
  [ProductFeature.report]: {
    titleKey: 'billing.checkoutProductDialog.overview.reports.title',
    subtitleKey: 'billing.checkoutProductDialog.overview.additionalDescription',
    bulletKeys: ['supportMeasures', 'gradeWeighting', 'development', 'roles', 'customization'],
  },
  [ProductFeature.supportMeasures]: {
    titleKey: 'billing.checkoutProductDialog.overview.supportMeasures.title',
    subtitleKey: 'billing.checkoutProductDialog.overview.additionalDescription',
    bulletKeys: ['reports', 'gradeWeighting', 'development', 'roles', 'customization'],
  },
};

const emit = defineEmits<{
  continue: [];
  requestUpgrade: [];
}>();

const props = defineProps<{
  disabled?: boolean;
  canManageBilling: boolean;
  feature: Components.Schemas.ProductFeature | null;
  trialDiscountPercent: number;
}>();

const { t } = useI18n();

const overviewContent = computed(() => {
  const config = (props.feature ? overviewConfigByFeature[props.feature] : undefined) ?? fallbackOverviewConfig;
  return {
    title: t(config.titleKey),
    subtitle: config.subtitleKey ? t(config.subtitleKey) : null,
    bulletPoints: config.bulletKeys.map((bullet) => t(featureTranslationKeys[bullet])),
  };
});

const discountNotice = computed<string>(() => {
  if (props.trialDiscountPercent > 0) {
    return t('billing.products.trialDiscount.validDuringTrial', { percent: props.trialDiscountPercent });
  }

  return t('billing.checkoutProductDialog.overview.annualDiscount');
});

const onSubmit = (): void => {
  emit('continue');
};

defineExpose({
  onSubmit,
});
</script>

<template>
  <div class="flex flex-col items-center gap-xl px-[80px] pb-[80px] max-md:px-lg max-md:pb-mobile-safe-bottom-lg">
    <img
      src="/assets/icons/digidoo-upgrade.png"
      :alt="$t('imageAlt.digidooUpgradeIcon')"
      class="size-[80px] shrink-0"
    />

    <h1
      class="w-full whitespace-pre-line text-center text-title font-medium leading-[165%] tracking-[0.22px] text-primary"
    >
      {{ overviewContent.title }}
    </h1>

    <p v-if="overviewContent.subtitle" class="w-full text-center leading-[170%]">{{ overviewContent.subtitle }}</p>

    <ul class="flex w-full flex-col gap-sm">
      <li v-for="bulletPoint in overviewContent.bulletPoints" :key="bulletPoint" class="flex items-start gap-sm">
        <AnyIcon :icon-name="EIcon.CHECK_CIRCLE_SOLID" class="mt-[4px] size-icon-sm shrink-0 text-tint" />
        <span class="min-w-0 leading-[170%]">{{ bulletPoint }}</span>
      </li>
    </ul>

    <div class="flex w-full flex-col items-center gap-md">
      <CustomButton
        v-if="canManageBilling"
        class="w-[320px] max-w-full justify-center"
        :disabled="disabled"
        @click="emit('continue')"
      >
        {{ $t('billing.buttons.chooseSubscription') }}
      </CustomButton>
      <CustomButton v-else class="w-[320px] max-w-full justify-center" @click="emit('requestUpgrade')">
        {{ $t('billing.banner.actions.contactLead') }}
      </CustomButton>

      <p class="w-full text-center text-footer font-semibold leading-[160%] tracking-[0.12px] text-success">
        {{ discountNotice }}
      </p>
    </div>
  </div>
</template>
